import styled from 'styled-components';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* Telefon i kod QR to dwie osobne rzeczy — odstęp ma to pokazywać. */
  gap: 26px;
  /* Pełna wysokość treści szuflady — bez tego telefon wisiałby przy górnej krawędzi. */
  height: 100%;
  overflow: hidden;
`;

/**
 * Scena telefonu. Makieta jest rysowana w PRAWDZIWYCH wymiarach telefonu
 * (390 px szerokości ekranu), a do szuflady dopasowuje się skalowaniem — dzięki
 * temu aplikacja gościa układa się dokładnie tak, jak na telefonie, zamiast
 * renderować się w sztucznie wąskim oknie i wyglądać na ściśniętą.
 *
 * Scena ma rozmiar JUŻ PRZESKALOWANY — element z `transform` zajmuje w układzie
 * swoje pierwotne wymiary, więc bez tego szuflada dostałaby pasek przewijania.
 */
export const Stage = styled.div<{ $width: number; $height: number }>`
  flex-shrink: 0;
  width: ${({ $width }) => `${$width}px`};
  height: ${({ $height }) => `${$height}px`};
  overflow: hidden;
`;

export const StageInner = styled.div<{ $scale: number }>`
  /*
   * 'width: max-content' jest tu warunkiem działania, nie kosmetyką: scena ma
   * 'overflow: hidden' i rozmiar wyliczony ze skali, więc zwykły blok dostałby
   * szerokość rodzica (na starcie zero) i pomiar makiety zwracałby zero.
   */
  display: inline-block;
  width: max-content;
  transform: ${({ $scale }) => `scale(${$scale})`};
  transform-origin: top left;
`;

/**
 * Wnętrze ekranu telefonu: strona i pasek przeglądarki. Kolumna na pełnej
 * wysokości, bo to ona rozdziela miejsce między stronę a atrapę.
 */
export const Browser = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-self: stretch;
  min-width: 0;
  background: #f2f2f7;
`;

/**
 * Pasek stanu telefonu: godzina po lewej, wskaźniki po prawej. Rysujemy go SAMI
 * i trzymamy w normalnym układzie, bo biblioteka daje wyłącznie wyspę aparatu —
 * i to jako element pozycjonowany absolutnie, który ląduje dokładnie na środku
 * tego paska. Dlatego środek zostawiamy pusty, a treść rozsuwamy na boki.
 */
export const StatusBar = styled.div<{ $inset: number; $pad: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  height: ${({ $inset }) => `${$inset}px`};
  padding: ${({ $pad }) => `0 ${$pad}px`};
  background: #ffffff;
  color: #000000;
`;

export const StatusTime = styled.span`
  /* iOS używa tu SF Pro — na innych systemach dobieramy najbliższy krój systemowy. */
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.1px;
`;

export const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  /* Wymiary słupków zasięgu i baterii niosą własne viewBox-y — skalujemy tylko wifi. */
  > svg:nth-child(2) {
    width: 16px;
    height: 16px;
    stroke-width: 2.2;
  }
`;

/** Liczba obok baterii, tak jak w iOS z włączonym procentem naładowania. */
export const BatteryPercent = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
`;

/**
 * Biblioteka wstawia dzieci do kontenera 'display: flex; flex: 1', więc oprócz
 * wymiarów procentowych dokładamy 'flex' — inaczej przy zmianie wersji obudowy
 * ramka mogłaby zejść do zerowej wysokości.
 */
/**
 * Kadr strony. To TU przycinamy nadmiarową szerokość ramki, a nie na całym
 * `Browser`: tamten musi puszczać poza swoje granice nakładkę paska stanu,
 * która leży nad nim (ujemny `top`).
 */
export const ScreenClip = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/**
 * Ramka na całą szerokość ekranu — bez żadnego przycinania. Pasek przewijania
 * chowa sama aplikacja gościa w trybie `?preview=1`; poszerzanie ramki poza kadr
 * (próbowaliśmy) zabierało prawą krawędź treści i było widać, że coś ucięto.
 */
export const Frame = styled.iframe`
  /*
   * Pozycjonowanie absolutne, a NIE 'flex: 1'. W kontenerze flex to flex decyduje
   * o rozmiarze elementu i nadpisywał 'width', więc ramka miała dokładnie 100%
   * szerokości, a pasek przewijania zostawał w kadrze.
   */
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  border: 0;
  background: #ffffff;
`;

/**
 * Pasek przeglądarki NA DOLE, jak w Safari na iOS od wersji 15. Na górze być go
 * nie może: tam siedzi wyspa aparatu i zasłaniała adres.
 */
export const BottomBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  padding: 7px 8px 5px;
  background: #f2f2f7;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

export const UrlPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 11px;
  border-radius: 999px;
  background: #ffffff;
  font-size: 12px;
  color: #3c3c43;
  white-space: nowrap;
  overflow: hidden;

  > svg {
    flex-shrink: 0;
    width: 11px;
    height: 11px;
    opacity: 0.5;
  }

  > span {
    flex: 1;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/** Odświeżenie strony w ramce — jedyny działający element atrapy przeglądarki. */
export const ReloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: #3c3c43;
  cursor: pointer;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    color: #007aff;
  }
`;

export const BrowserNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-shrink: 0;
  height: 26px;
  padding: 0 6px;
  color: #007aff;

  svg {
    width: 15px;
    height: 15px;
  }

  /* Strzałki wstecz/dalej są nieaktywne, jak w świeżo otwartej karcie. */
  svg:nth-child(-n + 2) {
    opacity: 0.3;
  }
`;

/**
 * Stopka: kod QR i podpisy w JEDNYM rzędzie, nie jeden pod drugim — całość ma
 * się zmieścić na ekranie bez przewijania, a poziomo miejsca jest pod dostatkiem.
 */
export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  max-width: 430px;
`;

export const QrBox = styled.div`
  display: flex;
  flex-shrink: 0;
  padding: 5px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#dcdfe4')};
`;

export const FooterText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const ScanHint = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6b7280')};
`;

/** Znacznik builda aplikacji gościa — przychodzi z ramki przez 'postMessage'. */
export const Version = styled.span`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : '#9ca3af')};
`;

export const Empty = styled.p`
  margin: 24px 0;
  text-align: center;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6b7280')};
`;
